import React, { useCallback, useMemo, useState } from 'react'
import { t } from 'src/translations'

import {
  SavedAddress,
  useDeleteAddressMutation
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import { DropDownOption } from 'src/shared/components/common/dropdown/DropDownOption'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import Image from 'shared/components/common/Image'
import DropDown from 'shared/components/common/dropdown'
import { alertError, alertSuccess } from 'shared/js/alertUtils'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { AccountAddressModal } from './AccountAddressModal'
import { LocationIcon } from '@toasttab/buffet-pui-icons'
import {
  formatDeliveryAddress,
  getAddressDisplay
} from 'src/public/components/online_ordering/addressUtils'
import { DeleteAddressConfirmation } from './delete_address/DeleteAddressConfirmation'

export const ProfileAddresses = () => {
  const { customer } = useCustomer()
  const [editingAddress, setEditingAddress] = useState<SavedAddress>()
  const [deletingAddress, setDeletingAddress] = useState<SavedAddress | null>(
    null
  )
  const [addingNewAddress, setAddingNewAddress] = useState(false)
  const actionClicked = useCallback((addr: SavedAddress) => {
    setEditingAddress(addr)
    setAddressModalOpen(true)
  }, [])
  const [DeleteAddressMutation] = useDeleteAddressMutation()
  const ooClient = useOOClient()
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const hasSavedAddresses = useMemo(
    () => customer && customer.addresses && customer.addresses.length > 0,
    [customer]
  )

  const deleteAddress = useCallback(
    async (addressGuid: string) => {
      try {
        const { data } = await DeleteAddressMutation({
          variables: { input: { addressGuid } },
          client: ooClient
        })
        if (!data) {
          throw new Error('Data object is undefined')
        } else if (data.deleteAddress.__typename === 'DeleteAddressError') {
          throw new Error(data.deleteAddress.message)
        } else if (data.deleteAddress.__typename === 'DeleteAddressResponse') {
          alertSuccess(t('address-delete.success'))
        }
      } catch (e) {
        alertError(t('address-screen.error'))
        reportError(e)
      }
    },
    [DeleteAddressMutation, ooClient]
  )

  return (
    <div
      className='accountSection profileAddresses'
      data-testid='ProfileAddresses'
      tabIndex={0}
    >
      <div className='sectionHeader'>
        <h2 className='sectionTitle'>
          {t('profile-addresses.saved-addresses')}
        </h2>
        <button
          className='addAddressButton'
          onClick={() => setAddingNewAddress(true)}
          data-testid='add-address-btn'
          aria-label={t('profile-addresses.add-address')}
        >
          {t('profile-addresses.add-address')}
        </button>
      </div>
      {hasSavedAddresses ? (
        <>
          {editingAddress && (
            <AccountAddressModal
              isOpen={addressModalOpen}
              onClose={() => {
                setAddressModalOpen(false)
                setEditingAddress(undefined)
              }}
              selectedAddress={editingAddress}
              onSuccessfulUpdate={() => {
                setEditingAddress(undefined)
                setAddressModalOpen(false)
              }}
              initialScreen='address-confirm'
              action='edit'
            />
          )}
          {hasSavedAddresses && (
            <div className='listItemsContainer'>
              {customer?.addresses.map((addr) => {
                const { displayLabel, formattedAddress } =
                  getAddressDisplay(addr)

                return (
                  <div key={addr.guid} className='savedAddress'>
                    <div className='savedAddressContent'>
                      <LocationIcon accessibility='decorative' />
                      <div className='savedAddressWithLabel'>
                        <div className='savedAddressLabel'>{displayLabel}</div>
                        <div className='savedAddressLine' key={addr.guid}>
                          {formattedAddress}
                        </div>
                      </div>
                    </div>
                    <div className='actions'>
                      <DropDown
                        testId='address-actions'
                        hideArrow
                        label={
                          <Image
                            className='addressActionIcon'
                            alt={t('profile-addresses.address-actions')}
                            src='icons/kebab-menu.svg'
                          />
                        }
                      >
                        {({ close }) => (
                          <>
                            <DropDownOption
                              testId='edit-address'
                              onSelect={() => {
                                actionClicked(addr)
                                close()
                              }}
                              onClose={close}
                            >
                              {t('profile-addresses.edit')}
                            </DropDownOption>
                            <DropDownOption
                              testId='remove-address'
                              onSelect={() => {
                                setDeletingAddress(addr)
                                close()
                              }}
                              onClose={close}
                            >
                              {t('profile-addresses.remove')}
                            </DropDownOption>
                          </>
                        )}
                      </DropDown>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {deletingAddress && (
            <DeleteAddressConfirmation
              isOpen={!!deletingAddress}
              onClose={() => setDeletingAddress(null)}
              onCancel={() => setDeletingAddress(null)}
              onConfirm={async () => {
                await deleteAddress(deletingAddress.guid)
                setDeletingAddress(null)
              }}
              address={formatDeliveryAddress(deletingAddress.deliveryInfo, {
                full: true
              })}
            />
          )}
        </>
      ) : (
        <div className='emptySection'>
          <div className='iconContainer'>
            <LocationIcon accessibility='decorative' />
          </div>
          <div className='header'>
            {t('profile-addresses.no-saved-addresses')}
          </div>
          <div className='description'>
            {t('profile-addresses.addresses-will-appear-here')}
          </div>
        </div>
      )}
      {addingNewAddress && (
        <AccountAddressModal
          isOpen={addingNewAddress}
          onClose={() => {
            setAddressModalOpen(false)
            setAddingNewAddress(false)
          }}
          onSuccessfulUpdate={() => {
            setAddingNewAddress(false)
          }}
          selectedAddress={null}
          initialScreen='address-add'
          action='add'
        />
      )}
    </div>
  )
}
